import React, { useMemo, useState } from "react";
import { Button } from "reactstrap";

import CustomTable from "../../../../components/CustomTable";
import { fmtBRL, fmtInt } from "./formatos";

// Bloco "Cobertura de tabela" (PO1-3625, versão reduzida): destinos sem
// tabela (CT-es normais sem cálculo + cotações spot) por transportadora × UF ×
// cidade, com exportação. A média do estado ficou para uma próxima história.
// PO1-3659 (H3): NFs sem cálculo como origem própria; o filtro de transportadora
// e origem do link do alerta vem aplicado pelo backend (`origem` só escolhe as
// colunas mostradas).
const COLUNAS_ORIGEM = {
  cte: ["ctesSemCalculo", "valorCtesSemCalculo"],
  nf: ["nfsSemCalculo", "valorNfsSemCalculo"],
  spot: ["cotacoesSpot", "valorCotacoesSpot"],
};

export default function CoberturaTabela({ dados, origem, onExportar, exportando }) {
  const [filtro, setFiltro] = useState("");
  const linhas = useMemo(() => {
    const todas = (dados && dados.linhas) || [];
    const termo = filtro.trim().toLowerCase();
    return todas
      .filter((l) => !termo || `${l.transportadora} ${l.uf} ${l.cidade} ${l.regiao}`.toLowerCase().indexOf(termo) !== -1)
      .map((l, i) => ({ ...l, id: `${l.transportadora}-${l.uf}-${l.cidade}-${i}` }));
  }, [dados, filtro]);

  if (!dados) return null;
  const t = dados.totais || {};

  const colunas = [
    { dataField: "transportadora", text: "Transportadora", formatter: (c) => c || "—" },
    { dataField: "regiao", text: "Região", formatter: (c) => c || "—" },
    { dataField: "uf", text: "UF", formatter: (c) => c || "—" },
    { dataField: "cidade", text: "Cidade de destino", formatter: (c) => c || "—" },
    { dataField: "ctesSemCalculo", text: "CT-es sem cálculo", align: "right", headerAlign: "right", formatter: (c) => fmtInt(c) },
    { dataField: "valorCtesSemCalculo", text: "Frete sem cálculo", align: "right", headerAlign: "right", formatter: (c) => fmtBRL(c) },
    { dataField: "nfsSemCalculo", text: "NFs sem cálculo", align: "right", headerAlign: "right", formatter: (c) => fmtInt(c) },
    { dataField: "valorNfsSemCalculo", text: "Valor das NFs", align: "right", headerAlign: "right", formatter: (c) => fmtBRL(c) },
    { dataField: "cotacoesSpot", text: "Cotações spot", align: "right", headerAlign: "right", formatter: (c) => fmtInt(c) },
    { dataField: "valorCotacoesSpot", text: "Frete spot", align: "right", headerAlign: "right", formatter: (c) => fmtBRL(c) },
  ].filter((c) => {
    // Com origem do alerta, só as colunas daquela origem (as de identificação ficam).
    if (!origem || !COLUNAS_ORIGEM[origem]) return true;
    const todasDeOrigem = [].concat(COLUNAS_ORIGEM.cte, COLUNAS_ORIGEM.nf, COLUNAS_ORIGEM.spot);
    return todasDeOrigem.indexOf(c.dataField) === -1 || COLUNAS_ORIGEM[origem].indexOf(c.dataField) !== -1;
  });

  return (
    <>
      <div className="cr-cob-resumo">
        <div className="cr-status-card" style={{ "--cr-status-color": "#ffb236" }}>
          <div className="cr-status-card__label">Destinos sem tabela</div>
          <div className="cr-status-card__value">{fmtInt(dados.destinosSemTabela)}</div>
          <div className="cr-status-card__helper">Pares transportadora × cidade com frete sem tabela</div>
        </div>
        <div className="cr-status-card" style={{ "--cr-status-color": "#fd5d93" }}>
          <div className="cr-status-card__label">CT-es sem cálculo</div>
          <div className="cr-status-card__value">{fmtInt(t.ctesSemCalculo)}</div>
          <div className="cr-status-card__helper">{fmtBRL(t.valorCtesSemCalculo)} de frete realizado sem tabela</div>
        </div>
        <div className="cr-status-card" style={{ "--cr-status-color": "#ff8d72" }}>
          <div className="cr-status-card__label">NFs sem cálculo</div>
          <div className="cr-status-card__value">{fmtInt(t.nfsSemCalculo)}</div>
          <div className="cr-status-card__helper">{fmtBRL(t.valorNfsSemCalculo)} em notas sem tabela para simular</div>
        </div>
        <div className="cr-status-card" style={{ "--cr-status-color": "#1d8cf8" }}>
          <div className="cr-status-card__label">Cotações spot</div>
          <div className="cr-status-card__value">{fmtInt(t.cotacoesSpot)}</div>
          <div className="cr-status-card__helper">{fmtBRL(t.valorCotacoesSpot)} cotados fora de tabela</div>
        </div>
        <div className="cr-status-card" style={{ "--cr-status-color": "#00bf9f" }}>
          <div className="cr-status-card__label">Exportar</div>
          <div className="cr-status-card__helper" style={{ marginTop: 10 }}>
            Lista por transportadora × UF × cidade e origem para enviar às transportadoras.
          </div>
          <Button color="success" size="sm" style={{ marginTop: 10, alignSelf: "flex-start" }} disabled={exportando || (dados.linhas || []).length === 0} onClick={onExportar}>
            <i className="tim-icons icon-cloud-download-93" /> {exportando ? "Gerando..." : "Exportar XLSX"}
          </Button>
        </div>
      </div>
      <div className="cr-controles" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
        <input
          type="search"
          className="form-control"
          style={{ maxWidth: 360 }}
          placeholder="Filtrar por transportadora, UF, região ou cidade"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      </div>
      {linhas.length === 0 ? (
        <div className="cr-state">Nenhum destino sem tabela no período{dados.filtro ? " para o filtro do alerta" : ""}.</div>
      ) : (
        <CustomTable data={linhas} columns={colunas} sizePerPage={15} showToolbar={false} themedPagination tableClasses="text-left" />
      )}
    </>
  );
}
